import { message, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BUTTON_SIZE, INPUT_SIZE } from "../../../common/ThemeConstants";
import FormButton from "../../../components/general/FormButton";
import FormCheckbox from "../../../components/general/FormCheckbox";
import FormContainer from "../../../components/general/FormContainer";
import FormMultiSelect from "../../../components/general/FormMultiSelect";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import ReportExcelDownload from "../../../components/ReportingComponents/ReportExcelDownload";
import { postRequest } from "../../../services/mainApp.service";
import { getDate } from "../../../functions/dateFunctions";
import WastageItemSummaryTemp from "./WastageItemSummaryTemp";
import { SearchOutlined } from "@ant-design/icons";
import { WASTAGE } from "../../../common/InventoryEnum";

const initialSearchValues = {
  OperationId: 1,
  CompanyId: null,
  BranchId: null,
  DepartmentId: null,
  DataType: null,
  DateFrom: "",
  DateTo: "",
  TransactionTypeId: WASTAGE,
  UserId: null,
  CategoryIds: [],
  SubCategoryIds: [],
  ItemIds: [],
  DateWise: false,
};

const WastageItemSummary = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [data, setData] = useState(initialSearchValues);
  const [list, setList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [disableExcel, setDisableExcel] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    postRequest(
      "/Inv_TransactionItemSummary",
      {
        OperationId: 1,
        CompanyId: userData.CompanyId,
        BranchId: null,
        DepartmentId: null,
        DataType: null,
        DateFrom: data.DateFrom || getDate(),
        DateTo: data.DateTo || getDate(),
        TransactionTypeId: WASTAGE,
        UserId: userData.UserId,
      },
      controller
    )
      .then((res) => {
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch dropdown data");
          return;
        }
        const additionalData = res?.data?.AdditionalData || {};
        const dataSet = res?.data?.DataSet || {};
        setBranchList(additionalData.Branches || dataSet.Table || []);
        setDepartmentList(additionalData.Departments || dataSet.Table1 || []);
        setCategoryList(additionalData.Categories || dataSet.Table2 || []);
        setSubCategoryList(additionalData.SubCategories || dataSet.Table3 || []);
        setItemList(additionalData.Items || dataSet.Table4 || []);
        setData((prev) => ({
          ...prev,
          DateFrom: prev.DateFrom || getDate(),
          DateTo: prev.DateTo || getDate(),
        }));
      })
      .catch((err) => {
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch dropdown data");
        }
      });
    return () => controller.abort();
  }, []);

  const handleSearchChange = (changeData) => {
    setData((prev) => ({ ...prev, [changeData.name]: changeData.value }));
    setList([]);
    setDisableExcel(true);
  };

  const handleMultiSelectChange = (name, values) => {
    setData((prev) => ({ ...prev, [name]: values || [] }));
    setList([]);
    setDisableExcel(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!data.DateFrom || !data.DateTo) {
      message.error("Please select Date From and Date To");
      return;
    }
    if (new Date(data.DateFrom) > new Date(data.DateTo)) {
      message.error("Date From must be less than or equal to Date To");
      return;
    }
    setLoading(true);
    const dataType = data.DateWise ? "WastageItemSummaryDateWise" : "WastageItemSummary";
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      BranchId: data.BranchId ?? null,
      DepartmentId: data.DepartmentId ?? null,
      DataType: dataType,
      DateFrom: data.DateFrom || getDate(),
      DateTo: data.DateTo || getDate(),
      TransactionTypeId: data.TransactionTypeId ?? WASTAGE,
      UserId: userData.UserId,
      CategoryIds:
        data.CategoryIds?.length > 0
          ? data.CategoryIds.join(",")
          : null,
      SubCategoryIds:
        data.SubCategoryIds?.length > 0
          ? data.SubCategoryIds.join(",")
          : null,
      ItemIds:
        data.ItemIds?.length > 0 ? data.ItemIds.join(",") : null,
    };
    postRequest("/Inv_TransactionItemSummary", payload)
      .then((res) => {
        setLoading(false);
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch report");
          setList([]);
          setDisableExcel(true);
          return;
        }
        const reportData = res?.data?.Data || res?.data?.DataSet?.Table || [];
        if (!reportData.length) {
          message.error("No records found!");
          setList([]);
          setDisableExcel(true);
          return;
        }
        setList(reportData);
        setDisableExcel(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch report");
        }
        setList([]);
        setDisableExcel(true);
      });
  };

  const filteredSubCategoryList =
    subCategoryList.filter(
      (x) =>
        !data.CategoryIds?.length || data.CategoryIds.includes(x.CategoryId)
    ) || [];
  const filteredItemList =
    itemList.filter(
      (x) =>
        (!data.CategoryIds?.length || data.CategoryIds.includes(x.CategoryId)) &&
        (!data.SubCategoryIds?.length ||
          data.SubCategoryIds.includes(x.SubCategoryId))
    ) || [];

  const searchPanel = (
    <Fragment>
      <FormSelect
        colSpan={6}
        listItem={branchList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={data.BranchId}
        onChange={handleSearchChange}
        allowClear={true}
      />
      <FormSelect
        colSpan={6}
        listItem={departmentList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={data.DepartmentId}
        onChange={handleSearchChange}
        allowClear={true}
      />
      <FormMultiSelect
        colSpan={6}
        listItem={categoryList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        label="Category"
        value={data.CategoryIds}
        onChange={(values) => handleMultiSelectChange("CategoryIds", values)}
      />
      <FormMultiSelect
        colSpan={6}
        listItem={filteredSubCategoryList}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        label="Sub Category"
        value={data.SubCategoryIds}
        onChange={(values) => handleMultiSelectChange("SubCategoryIds", values)}
      />
      <FormMultiSelect
        colSpan={6}
        listItem={filteredItemList}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        label="Item"
        value={data.ItemIds}
        onChange={(values) => handleMultiSelectChange("ItemIds", values)}
      />
      <FormTextField
        colSpan={6}
        label="Date From"
        type="date"
        name="DateFrom"
        size={INPUT_SIZE}
        value={data.DateFrom}
        onChange={handleSearchChange}
      />
      <FormTextField
        colSpan={6}
        label="Date To"
        type="date"
        name="DateTo"
        size={INPUT_SIZE}
        value={data.DateTo}
        onChange={handleSearchChange}
      />
      <FormCheckbox
        colSpan={6}
        name="DateWise"
        label="Date Wise"
        checked={data.DateWise}
        onChange={handleSearchChange}
      />
      <FormButton
        title="Search"
        type="primary"
        size={BUTTON_SIZE}
        colSpan={2}
        htmlType="submit"
        icon={<SearchOutlined />}
      />
    </Fragment>
  );

  return (
    <div style={{ margin: 5 }}>
      <Title level={3} style={{ color: "#017971" }}>
        {data.DateWise ? "Wastage Item Summary (Date Wise)" : "Wastage Item Summary"}
      </Title>
      <div
        style={{
          background: "white",
          padding: 15,
          boxShadow: "0 0 5px lightgray",
          borderRadius: 2,
        }}
      >
        <FormContainer
          rowStyle={{ alignItems: "flex-end" }}
          onSubmit={handleSearchSubmit}
        >
          {searchPanel}
        </FormContainer>
        <div style={{ margin: "10px 0", borderTop: "1px solid lightgray" }}></div>
      </div>
      <ReportExcelDownload
        fileName={`WastageItemSummary_Report_${data.DateFrom}_${data.DateTo}`}
      >
        {loading && (
          <Spin
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        )}
        {list.length > 0 && (
          <WastageItemSummaryTemp
            list={list}
            headList={list.length > 0 ? Object.keys(list[0]) : []}
            date={`From Date: ${data.DateFrom} To Date: ${data.DateTo}`}
            dateWise={data.DateWise}
            branch={
              branchList?.find(
                (x) => x.Id === data.BranchId || x.BranchId === data.BranchId
              )?.Name ||
              branchList?.find(
                (x) => x.Id === data.BranchId || x.BranchId === data.BranchId
              )?.BranchName ||
              "All"
            }
            department={
              departmentList?.find((x) => x.Id === data.DepartmentId)?.Name ||
              departmentList?.find(
                (x) => x.Id === data.DepartmentId
              )?.DepartmentName ||
              "All"
            }
          />
        )}
      </ReportExcelDownload>
    </div>
  );
};

export default WastageItemSummary;
